"use client";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

// Konami code sequence
const KONAMI_CODE = [
    "ArrowUp",
    "ArrowUp",
    "ArrowDown",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "ArrowLeft",
    "ArrowRight",
    "b",
    "a",
];

export default function KonamiBugHunt() {
    const [konamiTriggered, setKonamiTriggered] = useState(false);
    const [showMessage, setShowMessage] = useState(true);
    const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
    const [bugPos, setBugPos] = useState({ x: 0, y: 0 });
    const [_, setInputSequence] = useState<string[]>([]);
    const bugRef = useRef<HTMLImageElement>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Listen for Konami code
    useEffect(() => {
        const keyHandler = (e: KeyboardEvent) => {
            const key = e.key;
            setInputSequence((prev) => {
                const next = [...prev, key].slice(-KONAMI_CODE.length);
                if (next.join(",") === KONAMI_CODE.join(",")) {
                    setKonamiTriggered(true);
                    setShowMessage(true);
                    if (timerRef.current) clearTimeout(timerRef.current);
                    // Keep message visible for 6 seconds so user has time to read
                    timerRef.current = setTimeout(
                        () => setShowMessage(false),
                        6000,
                    );
                }
                return next;
            });
        };
        window.addEventListener("keydown", keyHandler);
        return () => window.removeEventListener("keydown", keyHandler);
    }, []);

    // Update bug and cursor position
    useEffect(() => {
        if (!konamiTriggered) return;

        setBugPos({
            x: Math.random() * (window.innerWidth - 80) + 40,
            y: Math.random() * (window.innerHeight - 80) + 40,
        });

        const moveHandler = (e: MouseEvent) =>
            setCursorPos({ x: e.clientX, y: e.clientY });
        window.addEventListener("mousemove", moveHandler);
        return () => window.removeEventListener("mousemove", moveHandler);
    }, [konamiTriggered]);

    if (!konamiTriggered) return null;

    return (
        <div className="fixed inset-0 z-[99999]">
            {/* Overlay with torch */}
            <div
                className="fixed inset-0 z-[9999] flex cursor-crosshair items-center justify-center"
                style={{
                    backgroundColor: "rgba(0,0,0,1)",
                    WebkitMaskImage: `radial-gradient(circle 160px at ${cursorPos.x}px ${cursorPos.y}px, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 70%)`,
                    maskImage: `radial-gradient(circle 160px at ${cursorPos.x}px ${cursorPos.y}px, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 70%)`,
                }}
                onClick={(e) => {
                    if (!bugRef.current) return;

                    const rect = bugRef.current.getBoundingClientRect();
                    const x = e.clientX;
                    const y = e.clientY;

                    if (
                        x >= rect.left &&
                        x <= rect.right &&
                        y >= rect.top &&
                        y <= rect.bottom
                    ) {
                        setKonamiTriggered(false);
                    }
                }}
            >
                {/* Torch ring highlight */}
                <div
                    className="pointer-events-none fixed h-[160px] w-[160px] rounded-full border-2 border-yellow-400/80 shadow-[0_0_20px_rgba(250,204,21,0.5)]"
                    style={{
                        left: cursorPos.x - 80,
                        top: cursorPos.y - 80,
                    }}
                />
            </div>

            {/* Bug Target (Rendered at z-[9990] behind darkness overlay z-[9999] so it is hidden until torch flashlight reveals it) */}
            <Image
                src="/images/bug.png"
                alt="bug"
                width={56}
                height={56}
                className="fixed z-[9990] animate-bounce cursor-pointer"
                style={{ left: bugPos.x, top: bugPos.y }}
                ref={bugRef}
            />

            {/* Message / Title (Horizontally centered, top vertically using website theme variables) */}
            <AnimatePresence>
                {showMessage && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                        className="fixed left-1/2 top-6 z-[100000] flex -translate-x-1/2 items-center gap-3 rounded-2xl border border-border-color bg-card-bg px-6 py-3.5 text-base font-semibold text-text-color shadow-2xl backdrop-blur-md md:text-lg"
                    >
                        <span className="flex items-center gap-2">
                            <span>🐛</span> Find the bug and click it to restore
                            light!
                        </span>
                        <button
                            onClick={() => setShowMessage(false)}
                            className="ml-2 rounded-lg border border-border-color bg-background-color px-2.5 py-1 text-xs text-label-color transition-colors hover:text-text-color"
                            title="Dismiss message"
                        >
                            ✕
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
